import { LightningElement, wire } from "lwc";
import { ApolloQueryResult } from "@apollo/client";
import { MultiLabelAdapter, LabelTranslations } from "tbme/localization";
import { QueryAdapter } from "tbme/queryAdapter";
import {
  GetEarnedSkills,
  GetEarnedSkillsVariables,
} from "./gql/GetEarnedSkills";
import { EarnedSkill } from "./gql/EarnedSkill";
import { EARNED_SKILLS_QUERY } from "./query";
import { CardState } from "../../tbui/card/types";
import { isRenderProfileChassis } from "tbme/localState";

// The number of skills to show when collapsed.
const initialVisible: number = 10;

export default class extends LightningElement {
  private isSelf: boolean = false;
  private skills: EarnedSkill[] = [];
  private displayAll: boolean = false;
  private cardState: CardState = "loading";
  private hasHeader: boolean = true;

  private variables: GetEarnedSkillsVariables = {
    trailblazerId: null,
    queryProfileById: false,
    queryProfileBySlug: false,
  };

  @wire(MultiLabelAdapter, {
    labels: [
      "skillsTitle",
      "skillsDescription",
      "skillsDescriptionThirdPerson",
      "showMore",
      "showLess",
    ],
  })
  private labels: LabelTranslations = {};

  @wire(QueryAdapter, {
    query: EARNED_SKILLS_QUERY,
    variables: "$variables",
  })
  private handleResult(result: ApolloQueryResult<GetEarnedSkills>) {
    const { data, loading, error, errors } = result;

    // The presence of the 'chassis' value indicates that we are running on chassis. The value of 'profileUser' may or
    // not be present in 'chassis' but is always present in the TBID Visualforce page. That's why the order of of operations
    // below is important: we must FIRST check if 'chassis' is present, if so query 'profile' by 'slug' and look no more.

    // If/when the id is resolved from local state, toggle the `queryProfileBySlug` variable which will trigger the
    // API call.
    if (
      isRenderProfileChassis() &&
      !this.variables.queryProfileBySlug &&
      !this.variables.queryProfileById &&
      data &&
      data.chassis
    ) {
      const username = data.chassis.profile?.username;
      const isSelf = data.chassis.isSelf;

      const queryProfileBySlug = !!username;
      this.isSelf = isSelf;

      if (queryProfileBySlug) {
        this.variables = {
          ...this.variables,
          queryProfileBySlug,
        };
      }
    }

    // If/when the id is resolved from local state, toggle the `queryProfileById` variable which will trigger the
    // API call.
    if (
      !isRenderProfileChassis() &&
      !this.variables.queryProfileBySlug &&
      !this.variables.queryProfileById &&
      data &&
      data.profileUser &&
      !data.chassis
    ) {
      const { id, isSelf } = data.profileUser;
      this.isSelf = isSelf;
      const queryProfileById = !!id;

      if (queryProfileById) {
        this.variables = this.mergeVariables({
          queryProfileById,
        });
      }
    }

    if (loading) {
      this.cardState = "loading";
      return;
    }

    if (error || errors) {
      this.cardState = "loadFailed";
      return;
    }

    if (this.variables.queryProfileBySlug) {
      if (
        !data.profileBySlug ||
        data.profileBySlug.__typename !== "PublicProfile"
      ) {
        this.cardState = "loadFailed";
        return;
      }

      const { profileBySlug } = data;
      if (
        !profileBySlug.earnedSkills ||
        profileBySlug.earnedSkills.length < 1
      ) {
        this.cardState = "default";
        return;
      }

      this.skills = profileBySlug.earnedSkills;
      this.cardState = "default";
    }

    if (this.variables.queryProfileById) {
      if (
        !data.profileById ||
        data.profileById.__typename !== "PublicProfile"
      ) {
        this.cardState = "loadFailed";
        return;
      }

      const { profileById } = data;
      if (!profileById.earnedSkills || profileById.earnedSkills.length < 1) {
        this.cardState = "default";
        return;
      }

      this.skills = profileById.earnedSkills;
      this.cardState = "default";
    }

    // If we are not loading anymore, and we don't have any errors and we were not able to set any of these 2 variables... there's an error.
    // We couldn't get neither the traiblazerId or the slug from the context
    if (
      !this.variables.queryProfileBySlug &&
      !this.variables.queryProfileById
    ) {
      this.cardState = "loadFailed";
      return;
    }
  }

  private get hasFooter(): boolean {
    return this.skills!.length > initialVisible;
  }

  private get subtitle(): string {
    return this.isSelf
      ? this.labels.skillsDescription
      : this.labels.skillsDescriptionThirdPerson;
  }

  private get shouldDisplayCard(): boolean {
    return !(this.cardState === "default" && this.skills.length < 1);
  }

  private get showMoreOrShowLess(): string {
    return this.displayAll ? this.labels.showLess : this.labels.showMore;
  }

  handleToggleShowMoreOrShowLess(): void {
    this.displayAll = !this.displayAll;
  }

  private mergeVariables(updates: Partial<GetEarnedSkillsVariables>) {
    return {
      ...this.variables,
      ...updates,
    };
  }
}
